import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Tag, 
  Filter, 
  X, 
  Search, 
  CheckCircle,
  Layers
} from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';

interface UserTag {
  id: string;
  name: string;
  description?: string;
  color: string;
  category: string;
  is_system: boolean;
  usage_count: number;
}

interface TagFilterProps {
  selectedTags: string[];
  onTagsChange: (tagIds: string[]) => void;
  dataType?: 'metric' | 'material' | 'carbon' | 'esg' | 'all';
  showCategoryFilter?: boolean;
  className?: string;
}

export const TagFilterComponent: React.FC<TagFilterProps> = ({
  selectedTags,
  onTagsChange,
  dataType = 'all',
  showCategoryFilter = true,
  className = ''
}) => {
  const { user } = useAuth();
  const [tags, setTags] = useState<UserTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadTags();
    }
  }, [user]);

  const loadTags = async () => {
    try {
      setLoading(true);
      
      // Load user tags from Supabase
      const { data: userTags, error: userTagsError } = await supabase
        .from('user_tags')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (userTagsError) throw userTagsError;

      // Load system tags (predefined tags)
      const { data: systemTags, error: systemTagsError } = await supabase
        .from('system_tags')
        .select('*')
        .order('name');

      if (systemTagsError) throw systemTagsError;

      // Combine and process tags
      const allTags = [
        ...(userTags || []).map(tag => ({ ...tag, is_system: false })),
        ...(systemTags || []).map(tag => ({ ...tag, is_system: true }))
      ];

      setTags(allTags);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tagId: string) => {
    const newSelectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId];
    
    onTagsChange(newSelectedTags);
  };

  const clearAllTags = () => {
    onTagsChange([]);
  };

  const getSelectedTagsInfo = () => {
    if (selectedTags.length === 0) return null;
    
    const selectedTagsData = tags.filter(tag => selectedTags.includes(tag.id));
    const categories = [...new Set(selectedTagsData.map(tag => tag.category))];
    
    return {
      count: selectedTags.length,
      categories: categories.slice(0, 2), // Show first 2 categories
      hasMoreCategories: categories.length > 2
    };
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tag.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tag.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(tags.map(tag => tag.category))];

  const selectedTagsInfo = getSelectedTagsInfo();

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Tag Filter Button */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className={`flex items-center gap-2 ${
              selectedTags.length > 0 ? 'border-blue-500 bg-blue-50 text-blue-700' : ''
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Tags</span>
            {selectedTags.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {selectedTags.length}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">Filter by Tags</h4>
              {selectedTags.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllTags}
                  className="h-6 px-2 text-xs"
                >
                  Clear All
                </Button>
              )}
            </div>
            
            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Category Filter */}
            {showCategoryFilter && (
              <div className="mb-3">
                <Label className="text-xs font-medium text-gray-600 mb-2 block">Category</Label>
                <div className="flex flex-wrap gap-1">
                  <Button
                    variant={selectedCategory === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory('all')}
                    className="h-6 px-2 text-xs"
                  >
                    All
                  </Button>
                  {categories.slice(0, 6).map(category => (
                    <Button
                      key={category}
                      variant={selectedCategory === category ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedCategory(category)}
                      className="h-6 px-2 text-xs"
                    >
                      {category}
                    </Button>
                  ))}
                  {categories.length > 6 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-xs"
                    >
                      +{categories.length - 6}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Tags List */}
          <ScrollArea className="max-h-64">
            <div className="p-4 space-y-2">
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Loading tags...</p>
                </div>
              ) : filteredTags.length === 0 ? (
                <div className="text-center py-4">
                  <Tag className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">No tags found</p>
                </div>
              ) : (
                filteredTags.map((tag) => (
                  <div
                    key={tag.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleTagToggle(tag.id)}
                  >
                    <Checkbox
                      checked={selectedTags.includes(tag.id)}
                      onChange={() => handleTagToggle(tag.id)}
                      className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{tag.name}</div>
                        {tag.description && (
                          <div className="text-xs text-gray-500 truncate">{tag.description}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Badge variant="outline" className="text-xs">
                        {tag.category}
                      </Badge>
                      {tag.is_system && (
                        <Badge variant="secondary" className="text-xs">System</Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Selected Tags Display */}
      {selectedTagsInfo && (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {selectedTagsInfo.categories.map(category => (
              <Badge key={category} variant="outline" className="text-xs">
                {category}
              </Badge>
            ))}
            {selectedTagsInfo.hasMoreCategories && (
              <Badge variant="outline" className="text-xs">
                +{selectedTagsInfo.count - 2} more
              </Badge>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllTags}
            className="h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
};
